import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { listQueue, removeFromQueue, queueCount } from "@/lib/offlineDb";
import axios from "axios";
import { currentActor } from "@/lib/sessionPrivacy";
import { securityHeaders } from "@/lib/securityHeaders";

const OfflineContext = createContext({
    isOnline: true,
    pendingCount: 0,
    syncing: false,
    syncNow: async () => {},
    refreshPending: async () => {},
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Standalone axios instance used by the sync drainer (we don't want to
// re-intercept queued requests through the offline middleware again).
const rawApi = axios.create({ baseURL: API_BASE, withCredentials: true });

export function OfflineProvider({ children }) {
    const [isOnline, setIsOnline] = useState(
        typeof navigator === "undefined" ? true : navigator.onLine,
    );
    const [pendingCount, setPendingCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const syncingRef = useRef(false);

    const refreshPending = useCallback(async () => {
        const n = await queueCount();
        setPendingCount(n);
        return n;
    }, []);

    const syncNow = useCallback(async () => {
        if (syncingRef.current) return;
        if (typeof navigator !== "undefined" && !navigator.onLine) return;
        syncingRef.current = true;
        setSyncing(true);
        try {
            const items = await listQueue();
            // Sort by id (FIFO)
            items.sort((a, b) => (a.id || 0) - (b.id || 0));
            for (const item of items) {
                const actor = currentActor();
                if (!actor) break;
                if (item.actor !== actor) { await removeFromQueue(item.id); continue; }
                try {
                    const headers = await securityHeaders(false);
                    if (actor !== currentActor()) break;
                    await rawApi.request({
                        method: item.method || "POST",
                        url: item.url,
                        data: item.data,
                        headers: { ...headers, "X-Session-Actor": actor },
                    });
                    await removeFromQueue(item.id);
                } catch (err) {
                    // 4xx → drop the bad request so the queue doesn't deadlock.
                    // 5xx / network → leave queued, abort the drain (retry later).
                    const status = err?.response?.status;
                    if (status && status >= 400 && status < 500) {
                        await removeFromQueue(item.id);
                        continue;
                    }
                    break;
                }
            }
        } finally {
            await refreshPending();
            syncingRef.current = false;
            setSyncing(false);
        }
        // Notify any open pages to refetch (e.g. FamilyTree listens for this).
        try {
            window.dispatchEvent(new CustomEvent("vt:offline-sync"));
        } catch (e) {
            console.debug("[OfflineContext] dispatchEvent failed:", e?.message || e);
        }
    }, [refreshPending]);

    // Track online/offline + initial pending count.
    useEffect(() => {
        const onOnline = () => {
            setIsOnline(true);
            // small delay so backend connections settle
            setTimeout(syncNow, 250);
        };
        const onOffline = () => setIsOnline(false);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        refreshPending();
        // Also listen for our internal "queued" event so banner updates immediately.
        const onQueued = () => refreshPending();
        window.addEventListener("vt:offline-queued", onQueued);
        window.addEventListener("vt:session-changed", onQueued);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
            window.removeEventListener("vt:offline-queued", onQueued);
            window.removeEventListener("vt:session-changed", onQueued);
        };
    }, [refreshPending, syncNow]);

    const value = useMemo(
        () => ({ isOnline, pendingCount, syncing, syncNow, refreshPending }),
        [isOnline, pendingCount, syncing, syncNow, refreshPending],
    );

    return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
    return useContext(OfflineContext);
}
